import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import { Button } from "bootstrap-4-react/lib/components";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepButton from "@material-ui/core/StepButton";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllMarketPlaces } from "../../../store/actions/marketplace.action";
import EbayStep from "./EbayStep";
import WalmartStep from "./WalmartStep";

/**
 * Map UI channel_id (from AddToMarketplaceModal's marketplace.types response)
 * to a step config. Channel IDs are strings per Tbl_Marketplace mapping in the
 * walmart-service marketplaceListing.service.
 */
const CHANNEL_TO_STEP = {
    "10000": { name: "eBay",    Component: EbayStep,    marketplaceName: "ebay",    implemented: true  },
    "10003": { name: "Walmart", Component: WalmartStep, marketplaceName: "walmart", implemented: false },
    // Amazon, Etsy, TikTok, Shopify will be added here as their adapters land.
};

const STATUS = {
    PENDING:     "pending",
    IN_PROGRESS: "in-progress",
    COMPLETED:   "completed",
    FAILED:      "failed",
    SKIPPED:     "skipped",
};

const AUTO_CLOSE_DELAY_MS = 2000;

/**
 * Per-step status icon for the Stepper. Returns undefined for "default"
 * states (pending / in-progress / completed) so material-ui falls back to
 * its built-in icon (numbered circle / green check).
 */
function renderStepIcon(status) {
    if (status === STATUS.FAILED) {
        return <i className="fa fa-times-circle" style={{ color: "#dc3545", fontSize: 22 }} />;
    }
    if (status === STATUS.SKIPPED) {
        return <i className="fa fa-minus-circle" style={{ color: "#999", fontSize: 22 }} />;
    }
    return undefined;
}

/**
 * Body content for the active step — either a success/skipped summary or
 * the step component itself. Extracted to avoid nested ternaries in the JSX.
 */
function renderStepBody({
    currentStep, currentStatus, result, StepComponent,
    product, marketplaceId, onPublishComplete, onSkip,
}) {
    if (currentStatus === STATUS.COMPLETED && result) {
        return (
            <div className="alert alert-success">
                <strong>{currentStep.name} listing published.</strong>
                {result.listingUrl && (
                    <p className="mb-0 mt-2">
                        <a href={result.listingUrl} target="_blank" rel="noreferrer">
                            View on {currentStep.name} →
                        </a>
                    </p>
                )}
            </div>
        );
    }
    if (currentStatus === STATUS.SKIPPED) {
        return (
            <div className="alert alert-secondary">
                {currentStep.name} was skipped.
            </div>
        );
    }
    return (
        <StepComponent
            product={product}
            marketplaceId={marketplaceId}
            onPublishComplete={onPublishComplete}
            onSkip={onSkip}
        />
    );
}

/**
 * MarketplacePushWizard
 *
 * Multi-step modal that walks the user through publishing an inventory item
 * to one or more selected marketplaces. Each step is independent — a failure
 * on eBay does not block Walmart, and vice versa.
 *
 * Wizard chrome (Stepper / Step / StepButton) mirrors the existing UserOnboarding
 * pattern so visual styling is consistent across the app.
 */
export default function MarketplacePushWizard({ show, onHide, product, selectedMarketplaceIds }) {
    const dispatch = useDispatch();
    // Tbl_Marketplace dropdown source — already used elsewhere; reuses the
    // /api/Consignment/GetAllMarketPlace endpoint (returns id + name).
    // The fetchAllMarketPlaces action runs the response through getSelectList(..., 'id', 'name'),
    // so the stored shape is { value: <id>, label: <name> } — NOT the raw entity.
    const marketPlacesList = useSelector((state) => state.InventoryManagement?.marketPlacesList) || [];

    const [activeStep, setActiveStep] = useState(0);
    const [stepStatuses, setStepStatuses] = useState({});  // channelId -> STATUS
    const [stepResults, setStepResults] = useState({});    // channelId -> publish result
    const autoCloseTimer = useRef(null);

    // Build the ordered step list from the user's picker selection.
    // Channel IDs not yet wired in CHANNEL_TO_STEP are silently dropped so
    // unsupported selections don't block the flow.
    const steps = useMemo(() => {
        return (selectedMarketplaceIds || [])
            .map(String)
            .filter((id) => CHANNEL_TO_STEP[id])
            .map((id) => ({ channelId: id, ...CHANNEL_TO_STEP[id] }));
    }, [selectedMarketplaceIds]);

    // Resolve the numeric Tbl_Marketplace.Id by name (case-insensitive).
    // Backend accepts either, but explicit numeric is the convention.
    // marketPlacesList is in { value, label } shape (see useSelector comment above).
    const marketplaceIdFor = (marketplaceName) => {
        const match = marketPlacesList.find(
            (m) => String(m.label || "").toLowerCase() === marketplaceName.toLowerCase(),
        );
        return match?.value || null;
    };

    // Load Tbl_Marketplace lookup once on open
    useEffect(() => {
        if (show) dispatch(fetchAllMarketPlaces());
        return () => {
            if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current);
        };
    }, [show, dispatch]);

    // Reset wizard state when reopened
    useEffect(() => {
        if (show) {
            setActiveStep(0);
            setStepStatuses(steps.reduce((acc, s) => ({ ...acc, [s.channelId]: STATUS.PENDING }), {}));
            setStepResults({});
        }
    }, [show, steps]);

    const completedCount = Object.values(stepStatuses).filter((s) => s === STATUS.COMPLETED).length;
    const allDone = steps.length > 0 && steps.every((s) => {
        const status = stepStatuses[s.channelId];
        return status === STATUS.COMPLETED || status === STATUS.FAILED || status === STATUS.SKIPPED;
    });

    /** Auto-close 2s after every step has reached a terminal state. */
    useEffect(() => {
        if (allDone && completedCount > 0) {
            autoCloseTimer.current = setTimeout(() => {
                onHide();
            }, AUTO_CLOSE_DELAY_MS);
        }
        return () => {
            if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current);
        };
    }, [allDone, completedCount, onHide]);

    const updateStatus = (channelId, status) => {
        setStepStatuses((prev) => ({ ...prev, [channelId]: status }));
    };

    const goToStep = (idx) => {
        // Allow navigation back to any step the user has already seen, but not
        // forward past unfinished work. Matches the UserOnboarding nonLinear pattern.
        if (idx < steps.length) setActiveStep(idx);
    };

    const goNext = () => {
        if (activeStep < steps.length - 1) setActiveStep(activeStep + 1);
    };

    const goBack = () => {
        if (activeStep > 0) setActiveStep(activeStep - 1);
    };

    const handleStepComplete = (channelId, result) => {
        setStepResults((prev) => ({ ...prev, [channelId]: result }));
        updateStatus(channelId, result?.success ? STATUS.COMPLETED : STATUS.FAILED);
    };

    const handleSkip = (channelId) => {
        updateStatus(channelId, STATUS.SKIPPED);
        if (activeStep < steps.length - 1) goNext();
    };

    /**
     * Close-warning policy:
     * - If at least one step is COMPLETED but at least one is still PENDING/IN_PROGRESS,
     *   warn before close. The user might lose track of partial success.
     * - If everything is PENDING (user just changed their mind) — no warning.
     * - If everything is COMPLETED/FAILED/SKIPPED (terminal) — no warning.
     */
    const shouldWarnOnClose = useMemo(() => {
        const statuses = Object.values(stepStatuses);
        const hasCompleted = statuses.includes(STATUS.COMPLETED);
        const hasPending = statuses.includes(STATUS.PENDING) || statuses.includes(STATUS.IN_PROGRESS);
        return hasCompleted && hasPending;
    }, [stepStatuses]);

    const requestClose = () => {
        if (shouldWarnOnClose) {
            const completedNames = steps
                .filter((s) => stepStatuses[s.channelId] === STATUS.COMPLETED)
                .map((s) => s.name)
                .join(", ");
            const pendingNames = steps
                .filter((s) => stepStatuses[s.channelId] === STATUS.PENDING || stepStatuses[s.channelId] === STATUS.IN_PROGRESS)
                .map((s) => s.name)
                .join(", ");
            const ok = window.confirm(
                `You published to ${completedNames} but ${pendingNames} ${pendingNames.includes(",") ? "are" : "is"} still pending. Close anyway?`,
            );
            if (!ok) return;
        }
        onHide();
    };

    if (!steps.length) {
        // Shouldn't normally render — picker filters to supported marketplaces —
        // but render a safe empty state in case selectedMarketplaceIds was empty.
        return null;
    }

    const currentStep = steps[activeStep];
    const currentStatus = stepStatuses[currentStep.channelId];
    const StepComponent = currentStep.Component;
    const marketplaceId = marketplaceIdFor(currentStep.marketplaceName);

    return (
        <Modal show={show} onHide={requestClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    Push to Marketplace
                    {steps.length > 1 && (
                        <span style={{ fontSize: 14, fontWeight: 400, color: "#666", marginLeft: 12 }}>
                            Step {activeStep + 1} of {steps.length}
                        </span>
                    )}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Stepper nonLinear activeStep={activeStep} style={{ padding: "0 0 16px 0" }}>
                    {steps.map((step, idx) => {
                        const status = stepStatuses[step.channelId];
                        const completed = status === STATUS.COMPLETED;
                        return (
                            <Step key={step.channelId} completed={completed}>
                                <StepButton
                                    onClick={() => goToStep(idx)}
                                    className="stepBtn"
                                    icon={renderStepIcon(status)}
                                >
                                    {step.name}
                                    {!step.implemented && (
                                        <span style={{ fontSize: 11, color: "#999", display: "block" }}>(coming soon)</span>
                                    )}
                                </StepButton>
                            </Step>
                        );
                    })}
                </Stepper>

                <div style={{ marginTop: 16 }}>
                    {renderStepBody({
                        currentStep,
                        currentStatus,
                        result: stepResults[currentStep.channelId],
                        StepComponent,
                        product,
                        marketplaceId,
                        onPublishComplete: (result) => handleStepComplete(currentStep.channelId, result),
                        onSkip: () => handleSkip(currentStep.channelId),
                    })}
                </div>

                {allDone && completedCount > 0 && (
                    <div className="text-muted text-center mt-3" style={{ fontSize: 12 }}>
                        Closing automatically…
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button className="cus-primary-transparent-btn" onClick={requestClose}>
                    {allDone ? "Close" : "Cancel"}
                </Button>
                {steps.length > 1 && activeStep > 0 && (
                    <Button className="cus-primary-transparent-btn" onClick={goBack}>
                        Back
                    </Button>
                )}
                {steps.length > 1 && activeStep < steps.length - 1 && (
                    <Button
                        className="cus-seconday-bg-btn"
                        onClick={goNext}
                        disabled={
                            currentStatus !== STATUS.COMPLETED &&
                            currentStatus !== STATUS.SKIPPED &&
                            currentStatus !== STATUS.FAILED
                        }
                    >
                        Next
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}

MarketplacePushWizard.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    product: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        Id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    selectedMarketplaceIds: PropTypes.arrayOf(PropTypes.string),
};
MarketplacePushWizard.defaultProps = { product: null, selectedMarketplaceIds: [] };
